import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-apollo';
import { gql } from '@apollo/client';
import LogoCatbears from '../../components/LogoCatbears/LogoCatbears';
import TopNavbar from '../../components/TopNavbar/TopNavbar';
import { isSubscriptionActive, getNextBillingDate, getDetailedSubscriptionStatus } from '../../utils/subscriptionUtils';
import { GlobalContext } from '../../context/GlobalContext';
import './Settings.scss';

const Settings = ({ user, authContext }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { setIsPWallOpen } = useContext(GlobalContext);



  // Show subscription/plan UI by default
  const [isOpenToPayments, setIsOpenToPayments] = useState(true)
  
  // Define the GraphQL query using gql
  const GET_SUBSCRIPTION_DETAILS = gql`
    query GetSubscriptionDetails {
      getSubscriptionDetails {
        id
        plan_id
        status
        lastPayment
        nextBillingTime
        price
        currency
        planName
        planType
      }
    }
  `;

  // Fetch subscription details with price from PayPal
  const { data: subscriptionData, loading: subscriptionLoading, error: subscriptionError } = useQuery(
    GET_SUBSCRIPTION_DETAILS,
    {
      skip: !user?.subscription?.id,
      errorPolicy: 'all'
    }
  );

  // Debug: Log query status
  // console.log('Settings - Query skip condition:', !user?.subscription?.id);
  // console.log('Settings - User subscription ID:', user?.subscription?.id);
  // console.log('Settings - User subscription plan_id:', user?.subscription?.plan_id);
  // console.log('Settings - Query loading:', subscriptionLoading);
  // console.log('Settings - Query error:', subscriptionError);

  // Debug: Log subscription data
  // console.log('Settings - User subscription data:', user?.subscription);
  // console.log('Settings - PayPal subscription details:', subscriptionData?.getSubscriptionDetails);
  // console.log('Settings - isSubscriptionActive result:', isSubscriptionActive(user?.subscription));
  // console.log('Settings - User object:', user);

  const handleForgotPassword = () => {
    history.push('/forgot-password');
  };

  const handleLogout = () => {
    if (authContext && typeof authContext.logout === 'function') {
      authContext.logout();
      return;
    }
    // Fallback (shouldn't be used): emulate same behavior as global handler
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    } catch (_) {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    const path = window.location.pathname || '';
    if (path.startsWith('/felt')) {
      window.location.replace('/felt');
    } else {
      window.location.replace('/');
    }
  };


  const handleLearnMore = () => {
    // Open the PayWall over the current page
    setIsPWallOpen(true);
    // console.log('Opening PayWall for premium plans');
  };

  const handleCancelSubscription = async () => {
    // Show confirmation dialog
    const isConfirmed = window.confirm(t('settings.cancelSubscriptionConfirm'));
    
    if (!isConfirmed) {
      return; // User cancelled the confirmation
    }
    
    try {
      // console.log('🔍 Settings: Canceling subscription...');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          query: `
            mutation CancelSubscription {
              cancelSubscription {
                success
                message
                subscriptionId
              }
            }
          `
        })
      });
      
      const data = await response.json();
      // console.log('🔍 Settings: Cancel subscription response:', data);
      
      if (data.data?.cancelSubscription?.success) {
        alert(t('settings.cancelSubscriptionSuccess'));
        // Refresh the page to update the subscription status
        window.location.reload();
      } else {
        alert(t('settings.cancelSubscriptionError'));
      }
    } catch (error) {
      console.error('🔍 Settings: Error canceling subscription:', error);
      alert(t('settings.cancelSubscriptionNetworkError'));
    }
  };

  return (
    <div className="settings-page">
      <TopNavbar
        showLogo={false}
        hideLogoOnMobile={true}
        removeNavStrip={false}
        backgroundColor={false}
        border={false}
        BackOnClick={() => history.goBack()}
        user={user}
        authContext={authContext}
      />
      {/* Simple Header */}
      <div className="settings-header">
        <div className="header-content">
          <LogoCatbears 
            isTapToLandingPage={true}
            isSoundSwitch={false}
            isFeedbackBtn={false}
            hideLogoOnMobile={false}
            isCentered={false}
          />
          <div className="page-title">{t('settings.title')}</div>
          {/* Return to Home button removed per request */}
        </div>
      </div>

      {/* Main Content */}
      <div className="settings-content">
        <div className="content-wrapper">

          {/* Your Plan */}
          {isOpenToPayments &&
          <div className="settings-section">
            <h2 className="section-title">{t('settings.yourPlan', 'Your Plan')}</h2>
            
            {(() => {
              const subscriptionStatus = getDetailedSubscriptionStatus(user?.subscription);
              
              // Special handling for guest users with no subscription
              if (user?.isGuest && (!user?.subscription || !user?.subscription?.id)) {
                return (
                  <>
                    <p className="section-description">
                      {t('settings.freeTrialDescription')}
                    </p>
                    <button 
                      className="link-button"
                      onClick={handleLearnMore}
                    >
                      {t('settings.learnAboutPremium')}
                    </button>
                  </>
                );
              }
              
              if (subscriptionStatus.type === 'noSubscription') {
                // User has no subscription (free trial)
                return (
                  <>
                    <p className="section-description">
                      {t('settings.freeTrialDescription')}
                    </p>
                    <button 
                      className="link-button"
                      onClick={handleLearnMore}
                    >
                      {t('settings.learnAboutPremium')}
                    </button>
                  </>
                );
              }
              
              // User has some form of subscription
              return (
                <>
                  <div className="subscription-info">
                    <div className="info-row">
                      <span className="info-label">{t('settings.status')}:</span>
                      <span className={`info-value subscription-status-${subscriptionStatus.type}`}>
                        {subscriptionStatus.type === 'subscribed' && t('settings.subscribed')}
                        {subscriptionStatus.type === 'canceledStillActive' && t('settings.canceledStillActive')}
                        {subscriptionStatus.type === 'canceledExpired' && t('settings.canceledExpired')}
                        {subscriptionStatus.type === 'noSubscription' && t('settings.noSubscription')}
                        {!['subscribed', 'canceledStillActive', 'canceledExpired', 'noSubscription'].includes(subscriptionStatus.type) && subscriptionStatus.message}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">{t('settings.plan')}:</span>
                      <span className="info-value">
                        {subscriptionLoading ? t('settings.loading') : 
                         subscriptionData?.getSubscriptionDetails?.planName || 
                         (subscriptionData?.getSubscriptionDetails?.planType === 'monthly' ? t('settings.monthly') : 
                          subscriptionData?.getSubscriptionDetails?.planType === 'yearly' ? t('settings.yearly') : 
                          t('settings.unknownPlan'))}
                      </span>
                    </div>
                    {subscriptionData?.getSubscriptionDetails?.price && (
                      <div className="info-row">
                        <span className="info-label">{t('settings.price')}:</span>
                        <span className="info-value">
                          {subscriptionLoading ? t('settings.loading') :
                           `${subscriptionData.getSubscriptionDetails.currency} ${subscriptionData.getSubscriptionDetails.price}`}
                        </span>
                      </div>
                    )}
                    {(!subscriptionData?.getSubscriptionDetails?.price && !subscriptionLoading) && (
                      <div className="info-row">
                        <span className="info-label">{t('settings.price')}:</span>
                        <span className="info-value error-text">
                          {t('settings.priceUnavailable')}
                        </span>
                      </div>
                    )}
                    
                    {/* Show different date information based on status */}
                    {subscriptionStatus.type === 'subscribed' && (
                      <div className="info-row">
                        <span className="info-label">{t('settings.nextBilling')}:</span>
                        <span className="info-value">
                          {subscriptionLoading ? t('settings.loading') :
                           subscriptionData?.getSubscriptionDetails?.nextBillingTime ? 
                           getNextBillingDate({ nextBillingTime: subscriptionData.getSubscriptionDetails.nextBillingTime }) : 
                           t('settings.noDateSet')}
                        </span>
                      </div>
                    )}
                    
                    {subscriptionStatus.type === 'canceledStillActive' && (
                      <div className="info-row">
                        <span className="info-label">{t('settings.planWillEnd')}:</span>
                        <span className="info-value">
                          {subscriptionLoading ? t('settings.loading') :
                           subscriptionData?.getSubscriptionDetails?.nextBillingTime ? 
                           getNextBillingDate({ nextBillingTime: subscriptionData.getSubscriptionDetails.nextBillingTime }) : 
                           t('settings.noDateSet')}
                        </span>
                      </div>
                    )}
                    
                    {subscriptionStatus.type === 'canceledExpired' && (
                      <div className="info-row">
                        <span className="info-label">{t('settings.planEnded')}:</span>
                        <span className="info-value">
                          {subscriptionLoading ? t('settings.loading') :
                           subscriptionData?.getSubscriptionDetails?.nextBillingTime ? 
                           getNextBillingDate({ nextBillingTime: subscriptionData.getSubscriptionDetails.nextBillingTime }) : 
                           t('settings.noDateSet')}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Show cancel button only for active subscriptions */}
                  {subscriptionStatus.type === 'subscribed' && (
                    <button 
                      className="link-button"
                      onClick={handleCancelSubscription}
                    >
                      {t('settings.cancelSubscription')}
                    </button>
                  )}
                  
                  {/* Show reactivate button for expired subscriptions */}
                  {subscriptionStatus.type === 'canceledExpired' && (
                    <div className="subscription-actions">
                      <button 
                        className="reactivate-subscription-button"
                        onClick={handleLearnMore}
                      >
                        {t('settings.reactivateSubscription')}
                      </button>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          }

          
        </div>
      </div>
    </div>
  );
};

export default Settings; 